import React, { useEffect } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Box, Button, Grid, Paper, Tab, Tabs, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CircularProgress from '@mui/material/CircularProgress'
import Refresh from '@mui/icons-material/Refresh'

import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { useGetKycArchiveApplicationQuery } from '../../../api-services/kyc-archive/kycArchiveApi'
import { type AcuantApplicationDetailDto } from '../../../api-schema-types/kyc-archive/KycArchiveDtos'
import {
  resetKycArchiveApplication,
  selectCurrentApplicationId,
  selectTabIndex,
  setCurrentApplicationId,
  setTabIndex
} from './kycArchiveSlice'
import LoadingComponent from '../../components/ui-widgets/LoadingComponent'
import ErrorComponent from '../../components/ui-widgets/ErrorComponent'
import { RtkQueryHelper } from '../../../api-services/shared/RtkQueryHelper'
import { KycArchiveResultChip } from './KycArchiveResultChip'
import { KycArchivePartialRecordChip } from './KycArchivePartialRecordChip'
import KycArchiveOverviewView from './KycArchiveOverviewView'
import KycArchiveEvaluationsView from './KycArchiveEvaluationsView'
import KycArchiveHistoryView from './KycArchiveHistoryView'
import KycArchiveDocumentsView from './KycArchiveDocumentsView'

export interface KycArchiveViewProps {
  application: AcuantApplicationDetailDto
  applicationId: string
}

const KycArchiveApplicationPage: React.FC = () => {
  const params = useParams<any>()
  const history = useHistory()
  const dispatch = useAppDispatch()

  const currentApplicationId = useAppSelector<string | null>(selectCurrentApplicationId)
  const tabIndex = useAppSelector(selectTabIndex)

  let applicationId: string | null = null
  if (Boolean(params.applicationId) && params.applicationId !== '') {
    applicationId = params.applicationId
  }

  // ================================================================================================================
  // GET Query

  const skipQuery = applicationId === null
  const getApplicationApiResult = useGetKycArchiveApplicationQuery(applicationId ?? '', { skip: skipQuery })
  const refetchApplication = getApplicationApiResult.refetch

  const application: AcuantApplicationDetailDto | null = getApplicationApiResult.data ?? null

  useEffect(() => {
    if (currentApplicationId !== applicationId) {
      dispatch(resetKycArchiveApplication())
      dispatch(setCurrentApplicationId(applicationId))
    }
  }, [getApplicationApiResult.isSuccess, currentApplicationId, applicationId, dispatch])

  // ================================================================================================================
  // Handlers

  const handleRefresh = async (): Promise<void> => {
    await refetchApplication()
  }

  const handleTabSelected = (event: React.ChangeEvent<unknown>, newValue: number): void => {
    dispatch(setTabIndex(newValue))
  }

  const isApplicationDataLoaded = (): boolean => {
    if (getApplicationApiResult.error != null) return false
    if (getApplicationApiResult.isLoading) return false
    if (getApplicationApiResult.isFetching) return false
    return true
  }

  // ================================================================================================================

  return (
    <>
      <Grid container>
        <Grid item lg={12}>
          <Grid container sx={{ mb: 4 }}>
            <Grid item lg={8}>
              <Typography variant={'h5'}>
                {(application !== null)
                  ? (
                    <>
                      {`${application.firstName ?? ''} ${application.lastName ?? ''}`} — {application.email ?? '-'}
                      <Box sx={{ display: 'inline-block', ml: 2 }}>
                        <KycArchiveResultChip result={application.result}/>
                      </Box>
                      {application.isReconstructed && (
                        <Box sx={{ display: 'inline-block', ml: 1 }}>
                          <KycArchivePartialRecordChip/>
                        </Box>
                      )}
                    </>
                    )
                  : 'KYC Archive Application'
                }
              </Typography>
            </Grid>
            <Grid item lg={4} sx={{ textAlign: 'right' }}>
              <Button variant={'outlined'} onClick={() => history.push('/app/kyc-archive')} startIcon={<ArrowBackIcon/>}>
                Back to search
              </Button>
            </Grid>
          </Grid>

          <Paper>
            <Box sx={{ p: 4, mt: 3 }}>
              {getApplicationApiResult.isLoading && <LoadingComponent/>}
              {(getApplicationApiResult.error != null) &&
                <ErrorComponent message={RtkQueryHelper.mapResponseError(getApplicationApiResult.error)}/>}
              <Grid container>
                <Grid item lg={12} style={{ textAlign: 'right' }}>
                  <Button variant={'outlined'} onClick={handleRefresh}>
                    { !isApplicationDataLoaded() && <CircularProgress size={22} />}
                    { isApplicationDataLoaded() && <Refresh/>}
                  </Button>
                </Grid>
              </Grid>

              { isApplicationDataLoaded() && application !== null && applicationId !== null && (
                <>
                  <Tabs value={tabIndex} onChange={handleTabSelected} indicatorColor="primary" textColor="primary" centered>
                    <Tab label="Overview"/>
                    <Tab label="Evaluations"/>
                    <Tab label="History & Notes"/>
                    <Tab label="Documents"/>
                  </Tabs>

                  {tabIndex === 0 && (
                    <KycArchiveOverviewView application={application} applicationId={applicationId}/>
                  )}

                  {tabIndex === 1 && (
                    <KycArchiveEvaluationsView application={application} applicationId={applicationId}/>
                  )}

                  {tabIndex === 2 && (
                    <KycArchiveHistoryView application={application} applicationId={applicationId}/>
                  )}

                  {tabIndex === 3 && (
                    <KycArchiveDocumentsView application={application} applicationId={applicationId}/>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}

export default KycArchiveApplicationPage
